import type { SvgIconComponent } from "@mui/icons-material";
import {
  SpaceDashboardOutlined,
  HandymanOutlined,
  HistoryEduOutlined,
  FolderOpenOutlined,
  ShieldOutlined,
  HomeWorkOutlined,
  EngineeringOutlined,
} from "@mui/icons-material";

/**
 * The property dashboard's sidebar, in Asher's order (Sep 22 call). Every tab
 * is visible to every viewer — gating happens inside a tab, never by hiding
 * it from the menu. "Find a Trusted Pro" is a separate destination below the
 * tabs: it's the click-through revenue path and is never gated.
 */
export type DashboardTabId =
  | "home-status"
  | "maintenance"
  | "home-history"
  | "documents"
  | "protection"
  | "property-details"
  | "find-a-pro";

export interface DashboardTab {
  id: DashboardTabId;
  label: string;
  icon: SvgIconComponent;
}

export const DASHBOARD_TABS: DashboardTab[] = [
  { id: "home-status", label: "Home Status", icon: SpaceDashboardOutlined },
  { id: "maintenance", label: "Maintenance", icon: HandymanOutlined },
  { id: "home-history", label: "Home History", icon: HistoryEduOutlined },
  { id: "documents", label: "Documents", icon: FolderOpenOutlined },
  { id: "protection", label: "Protection", icon: ShieldOutlined },
  { id: "property-details", label: "Property Details", icon: HomeWorkOutlined },
];

export const FIND_A_PRO_TAB: DashboardTab = {
  id: "find-a-pro",
  label: "Find a Trusted Pro",
  icon: EngineeringOutlined,
};

export const DEFAULT_TAB: DashboardTabId = "home-status";

const ALL_IDS = new Set<string>([...DASHBOARD_TABS.map((t) => t.id), FIND_A_PRO_TAB.id]);

// Links built before the rebuild — My Account, the homepage "Need Help?"
// cards, emails — must keep landing somewhere sensible.
const LEGACY_TAB_IDS: Record<string, DashboardTabId> = {
  "home-records": "home-history",
  records: "home-history",
  contracts: "home-history",
  "home-details": "property-details",
  details: "property-details",
  schools: "property-details",
  neighbourhood: "property-details",
  neighborhood: "property-details",
  climate: "property-details",
  "sales-tax": "property-details",
  insurance: "protection",
  bids: "find-a-pro",
  pro: "find-a-pro",
};

/**
 * Which tab a URL asks for. A `trade` param (the homepage "Request a service"
 * flow) always means the pro finder, whatever `tab` says — older links send
 * `?tab=home-records&trade=roofing`.
 */
export const resolveTabParam = (
  tab: string | null,
  trade: string | null
): DashboardTabId => {
  if (trade) return "find-a-pro";
  if (!tab) return DEFAULT_TAB;
  if (ALL_IDS.has(tab)) return tab as DashboardTabId;
  return LEGACY_TAB_IDS[tab] || DEFAULT_TAB;
};

export const tabLabel = (id: DashboardTabId): string =>
  id === FIND_A_PRO_TAB.id
    ? FIND_A_PRO_TAB.label
    : DASHBOARD_TABS.find((t) => t.id === id)?.label || "";
